import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';

const destinationFor = (user) => {
  if (user?.registered === false) return '/kyc';
  return user?.role === 'admin' ? '/admin/users' : '/dashboard';
};

const VerifyEmailPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { verifyEmail } = useAuth();
  const [state, setState] = useState({ loading: true, error: '', done: false });

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      const result = await verifyEmail(token);
      if (!mounted) return;

      if (result.success) {
        const destination = destinationFor(result.user);
        setState({ loading: false, error: '', done: true });
        window.setTimeout(() => {
          if (mounted) navigate(destination, { replace: true });
        }, 900);
      } else {
        setState({ loading: false, error: result.error, done: false });
      }
    };

    run();

    return () => {
      mounted = false;
    };
  }, [navigate, token, verifyEmail]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white border border-gray-200 rounded-lg p-8 shadow-sm text-center">
        {state.loading && (
          <>
            <Loader2 className="h-10 w-10 mx-auto text-burgundy-700 animate-spin mb-4" />
            <h1 className="font-serif text-2xl text-gray-950 mb-2">Verifying Email</h1>
            <p className="text-gray-600">Please wait while Magnolia activates your account.</p>
          </>
        )}

        {!state.loading && state.done && (
          <>
            <CheckCircle2 className="h-10 w-10 mx-auto text-green-700 mb-4" />
            <h1 className="font-serif text-2xl text-gray-950 mb-2">Email Verified</h1>
            <p className="text-gray-600 mb-6">Your Magnolia account is active. Taking you to your next step...</p>
            <Button asChild className="bg-burgundy-700 hover:bg-burgundy-800">
              <Link to="/kyc">Continue</Link>
            </Button>
          </>
        )}

        {!state.loading && state.error && (
          <>
            <XCircle className="h-10 w-10 mx-auto text-red-700 mb-4" />
            <h1 className="font-serif text-2xl text-gray-950 mb-2">We Could Not Verify This Link</h1>
            <p className="text-gray-600 mb-6">{state.error}</p>
            <Button asChild variant="outline">
              <Link to="/login">Back to sign in</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
